import type { LoyaltyHubCard, LoyaltyHubContent } from '~/types/loyalty-hub';
import { getAssetsUrlForCDN } from '@/utils/template';

// Resolução das artes do hub "Club Fidelidade" e ordenação dos cards.
//
// Vive aqui — e não dentro dos componentes — porque o store precisa das MESMAS
// URLs que a UI vai pedir, pra aquecer as artes do topo antes da primeira
// abertura (ver `warmAssets` em stores/loyalty-hub.ts). Se as duas pontas
// derivassem o caminho por conta própria, o aquecimento silenciosamente puxaria
// um arquivo diferente do que o <img> renderiza.

const CARDS_BASE = '/assets/images/loyalty-hub/cards';
const DEFAULT_HEADER_DESKTOP = '/assets/images/loyalty-hub/header-desktop.webp';

// Baú do card de cashback — o ÚNICO pedaço de imagem daquele card, agora que ele
// é composto em DOM (ver CashbackCard.vue). Um arquivo só serve os dois
// breakpoints: mobile e desktop mostram recortes de tamanhos diferentes do MESMO
// desenho (o CSS enquadra), então não existe versão /desktop dele.
const CASHBACK_ART = '/assets/images/loyalty-hub/cashback-chest.webp';

// O card de cashback é composto em DOM (gradiente/texto/CTA em CSS) em vez de
// vir como webp pré-composto. A checagem é por `id` porque `id` já é o que
// resolve a arte de qualquer card (`/cards/<id>.webp`) — o mesmo predicado serve
// o Card.vue (quem escolhe o componente) e a resolução de arte aqui embaixo, pra
// as duas pontas não divergirem.
export function isLoyaltyHubCashbackCard(card: LoyaltyHubCard) {
  return card.id === 'cashback';
}

export function loyaltyHubCashbackArt() {
  return getAssetsUrlForCDN(CASHBACK_ART);
}

// Fundo pré-composto do card (gradiente + arte + texto + CTA, com o bleed já
// embutido como transparência no topo). Mobile e desktop têm artes diferentes.
// O contrato pode sobrepor o MOBILE via `background`; o desktop segue sempre a
// convenção de caminho.
export function loyaltyHubCardArt(card: LoyaltyHubCard, desktop = false) {
  // Card composto devolve o herói dele, e não o webp pré-composto. Sai por AQUI
  // (e não só dentro do componente) porque o `warmAssets` do store aquece
  // exatamente o que esta função devolve — o cashback é um dos dois cards
  // aquecidos, e sem isto o hub baixaria ~55 KB (mobile) / ~117 KB (desktop) de
  // uma arte que ninguém mais renderiza.
  if (isLoyaltyHubCashbackCard(card)) return loyaltyHubCashbackArt();

  if (desktop) {
    return getAssetsUrlForCDN(`${CARDS_BASE}/desktop/${card.id}.webp`);
  }

  return getAssetsUrlForCDN(card.background ?? `${CARDS_BASE}/${card.id}.webp`);
}

// Arte da variante BLOQUEADA (o herói do card em escala de cinza). Um arquivo só
// pros dois breakpoints — diferente do card ativo, o bloqueado é composto em DOM
// (LockedCard.vue), então a mesma arte serve mobile e desktop.
// Sem `lockedImage` no contrato devolve `undefined`: é o que o `v-if` da arte
// observa. NÃO há caminho por convenção aqui de propósito — só o check-in tem
// arte bloqueada hoje, e derivar `/cards/locked/<id>.webp` faria todo card
// desativado pedir um arquivo que não existe.
export function loyaltyHubCardLockedArt(card: LoyaltyHubCard) {
  return card.lockedImage ? getAssetsUrlForCDN(card.lockedImage) : undefined;
}

// Header (imagem única: louros + "Seja bem-vindo a Clube Fidelidade" + hero).
// Devolve '' quando não há conteúdo — é o que o `v-if` do header observa.
export function loyaltyHubHeaderArt(
  content: LoyaltyHubContent | null | undefined,
  desktop = false,
) {
  if (!content) return '';

  if (desktop) {
    return getAssetsUrlForCDN(
      content.headerImageDesktop ?? DEFAULT_HEADER_DESKTOP,
    );
  }

  return content.headerImage ? getAssetsUrlForCDN(content.headerImage) : '';
}

// Ordem de exibição: `priority` menor primeiro, numa CÓPIA (a fonte continua
// intacta). Quem não tem `priority` cai pro fim, e o sort do JS é estável —
// então empate (inclusive entre os sem priority) preserva a ordem original.
// Cards desativados (`enabled: false`) NÃO são movidos nem removidos.
export function sortLoyaltyHubCards(
  cards: LoyaltyHubCard[] | undefined | null,
) {
  return [...(cards ?? [])].sort(
    (a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity),
  );
}

// Ordem do grid DESKTOP (`desktopPriority`), que é própria no Figma — o check-in
// abre a lista e a loja desce pra linha de 3. Parte da ordem do mobile, então
// card sem `desktopPriority` cai no fim preservando a posição relativa que tinha
// lá. Quem consome: o mapa de `order` do CSS (a ordem do DOM continua a do
// mobile) e o aquecimento das artes, que precisa saber quem aparece primeiro.
export function sortLoyaltyHubCardsDesktop(
  cards: LoyaltyHubCard[] | undefined | null,
) {
  return sortLoyaltyHubCards(cards).sort(
    (a, b) => (a.desktopPriority ?? Infinity) - (b.desktopPriority ?? Infinity),
  );
}
